import { apiGet, apiPost, apiPut } from "../api/client";

export interface Tile {
  label_en: string;
  label_ar: string;
  value: string;
}
export interface ForkRuleView {
  rule_id: string;
  summary: string | null;
  summary_ar?: string | null;
  origin: string | null;
  citation: string | null;
}
export interface ForkView {
  run_id: string;
  hand_key: string;
  hand_name: string;
  hand_name_ar?: string;
  verdict: string;
  created_at: string;
  blocking_rules: ForkRuleView[];
  missing: string[];
  missing_rules: ForkRuleView[];
  subject: { label: string; label_ar?: string; value: string } | null;
  decision: { key: string; label: string; label_ar?: string; stage: string; approver_role: string } | null;
  failed_validators: string[];
  model_called: boolean;
  resolution: { decision: string; reason: string; resolved_role: string; resolved_at: string; batch_id?: string | null } | null;
  approver_role: string;
}
export interface RunView {
  run_id: string;
  hand_key: string;
  hand_name: string;
  hand_name_ar?: string;
  verdict: string;
  created_at: string;
  headline_en: string | null;
  headline_ar: string | null;
  tiles: Tile[];
  pack_url: string | null;
  /** A pack as slides and PDF; a composed agent's own formats. Absent on older servers. */
  files?: { format: "pptx" | "xlsx" | "docx" | "pdf"; url: string }[];
  failed_at: string | null;
  checks_failed: string[];
  marking: "public" | "internal" | "confidential" | "regulated_pii";
  approve_effect: { en: string; ar: string };
  checks_failed_en: string[];
  checks_failed_ar: string[];
  review_draft_en: string | null;
  review_draft_ar: string | null;
  prepared?: {
    proposal: "approve" | "reject" | "needs_more_info" | "none";
    basis: { en: string; ar: string };
    group_key: string;
    similar_run_ids: string[];
  } | null;
  fork: ForkView | null;
  next_actor?: { role: string; words: { en: string; ar: string } } | null;
  missing_words?: { en: string; ar: string }[];
  evidence?: { kind: "phrase" | "series" | "case"; label: { en: string; ar: string }; value: string; detail?: { en: string; ar: string } }[];
  unmatched_numbers?: string[];
  rules_note?: { en: string; ar: string } | null;
  as_of?: string | null;
  rewrite?: { run_id: string; passed: boolean; copy_en: string | null; copy_ar: string | null; decided: boolean } | null;
}
export interface ChatMessage {
  id: string;
  kind: "user" | "system" | "result" | "fork" | "assistant";
  ref_run_id: string | null;
  body_en: string;
  body_ar: string;
  meta: Record<string, unknown>;
  created_at: string;
  run: RunView | null;
}
export interface Chip {
  hand_key: string;
  en: string;
  ar: string;
  cost_class: string;
}
export interface ThreadResponse {
  me: { name: string; role: string; role_label: string; role_label_ar?: string };
  workspace: { id: string; name: string };
  trust: { en: string; ar: string };
  chips: Chip[];
  messages: ChatMessage[];
}
export interface SendBody {
  text: string;
  chip?: string;
  answer_for?: string;
  attachment_id?: string;
  confirm_message_id?: string;
}

/**
 * An access request as a Supervisor sees it (agent-library Task 8): the
 * thread line's `meta.access_request`, and each entry of the Queue's
 * `access`. Its state is read from the request's row every time.
 */
export interface AccessCardView {
  request_id: string;
  agent_id: string;
  agent_name: { en: string; ar: string };
  requester_name: string;
  in_words: { en: string; ar: string };
  /** The five access lines stored on the request: what the requester saw. */
  access: { en: string[]; ar: string[] };
  /** "superseded": closed by nobody (the agent changed or was retired, or a library request was replaced). */
  status: "open" | "approved" | "rejected" | "superseded";
  /** A share (agent-library Task 13): the roles it would add, and them in words. Null for the agent's own access. */
  shared_with?: string[] | null;
  shared_with_words?: { en: string; ar: string } | null;
  /** A library request (agent-library Task 13b): the entry, and the roles that may reuse it. Null otherwise. */
  library?: { entry_id: string; roles: string[]; roles_words: { en: string; ar: string } } | null;
  /** The agent changed what it reads since: this request can't be decided. */
  stale: boolean;
  /** Superseded without the agent's access moving (retired, replaced, taken out): said as "closed", not "changed". */
  closed?: boolean;
  decided_by_name: string | null;
  reason: string | null;
  created_at: string;
}

const base = (ws: string) => `/api/chat/workspaces/${ws}`;
export const chatApi = {
  thread: (ws: string) => apiGet<ThreadResponse>(`${base(ws)}/thread`),
  send: (ws: string, body: SendBody) => apiPost<{ messages: ChatMessage[] }>(`${base(ws)}/send`, body),
  resolve: (ws: string, runId: string, body: { decision: string; reason: string; release_failed_draft?: boolean; code?: string; run_again?: boolean }) =>
    apiPost<{ run_id: string; decision: string }>(`${base(ws)}/forks/${runId}/resolve`, body),
  stepUp: (ws: string, runId: string) => apiPost<{ sent: boolean; expires_in_s: number; demo_code?: string }>(`${base(ws)}/forks/${runId}/step-up`, {}),
  batchResolve: (ws: string, body: { run_ids: string[]; decision: string; reason: string }) =>
    apiPost<{ batch_id: string; results: { run_id: string; ok: boolean; error?: string }[] }>(`${base(ws)}/forks/batch-resolve`, body),
  decisionEmails: (ws: string) => apiGet<{ decision_emails: DecisionEmails }>(`${base(ws)}/me/decision-emails`),
  setDecisionEmails: (ws: string, v: DecisionEmails) => apiPut<{ decision_emails: DecisionEmails }>(`${base(ws)}/me/decision-emails`, { decision_emails: v }),
  /** `access` is a Supervisor's only: open access requests, counted in `open` with the decisions. */
  queue: (ws: string) =>
    apiGet<{ forks: ForkView[]; runs?: Record<string, RunView>; access?: AccessCardView[]; open: number }>(`${base(ws)}/queue`),
  /** A Supervisor's decision on an access request; the reason is a sentence, as for forks. */
  decideAccess: (ws: string, requestId: string, body: { decision: "approved" | "rejected"; reason: string }) =>
    apiPost<{ request_id: string; status: string; agent_status: string }>(`${base(ws)}/access/${encodeURIComponent(requestId)}/decide`, body),
  reports: (ws: string) => apiGet<{ reports: ChatMessage[] }>(`${base(ws)}/reports`),
  topic: (ws: string) => apiPost<{ message: ChatMessage }>(`${base(ws)}/topic`, {}),
  /** Start a draft from a library template; the spec card arrives in the thread. */
  useTemplate: (ws: string, key: string) =>
    apiPost<{ agent_id: string; message: ChatMessage }>(`${base(ws)}/library/${encodeURIComponent(key)}/use`, {}),
  /** Ask the Supervisor for the access a draft needs. */
  requestAccess: (ws: string, agentId: string) =>
    apiPost<{ request_id: string; message: ChatMessage }>(`${base(ws)}/composed/${encodeURIComponent(agentId)}/request-access`, {}),
  /** "Share with…" on a spec card: the roles to share it with, put to the Supervisors (agent-library Task 13). */
  shareAgent: (ws: string, agentId: string, roles: string[]) =>
    apiPost<{ request_id: string; created: boolean; message: ChatMessage }>(`${base(ws)}/composed/${encodeURIComponent(agentId)}/share`, { roles }),
  /** "Use this" on an agent built by the team: the person's own copy; ready at once or asked for (Task 13b). */
  useTeamEntry: (ws: string, entryId: string) =>
    apiPost<{ agent_id: string; needs_access: boolean; message: ChatMessage }>(`${base(ws)}/library/team/${encodeURIComponent(entryId)}/use`, {}),
  /** "Publish to the library" on a spec card: who may reuse it; a builder's goes to the Supervisors (Task 13b). */
  publishAgent: (ws: string, agentId: string, roles: string[]) =>
    apiPost<{ entry_id: string; status: "pending" | "published"; request_id: string | null; message: ChatMessage }>(
      `${base(ws)}/composed/${encodeURIComponent(agentId)}/publish`, { roles }),
  /** Take an entry out of the library: new uses stop, copies keep running (Task 13b). */
  unpublishEntry: (ws: string, entryId: string) =>
    apiPost<{ entry_id: string; status: string; message: ChatMessage }>(`${base(ws)}/library/team/${encodeURIComponent(entryId)}/unpublish`, {}),
  /** A "Change something" chip on a spec card: the chip's key and the card's version. */
  editAgent: (ws: string, agentId: string, body: { key: string; version?: number }) =>
    apiPost<{ card: unknown; access_changed: boolean; request_id?: string }>(`${base(ws)}/composed/${encodeURIComponent(agentId)}/edit`, body),
  attach: async (ws: string, file: File): Promise<{ attachment_id: string; chars: number; name: string }> => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${base(ws)}/attachments`, { method: "POST", body: fd, credentials: "include" });
    if (!res.ok) {
      const b = (await res.json().catch(() => ({}))) as { detail?: string };
      throw new Error(b.detail ?? "That file could not be read.");
    }
    return (await res.json()) as { attachment_id: string; chars: number; name: string };
  },
};

export interface RunStory {
  view: RunView;
  took_seconds: number;
  model: string | null;
  rules_live: boolean;
  pack_version: string | null;
  checks: { rule_id: string; summary: string; summary_ar?: string; outcome: "passed" | "stopped" | "flagged" | "needs_person"; source: string | null }[];
  checks_failed_en: string[];
  checks_failed_ar: string[];
  figures_substituted: number;
  draft_en: string | null;
  draft_ar: string | null;
  answer?: { lookups: { label: { en: string; ar: string }; detail: string; found: boolean }[] };
}
export const storyApi = {
  get: (ws: string, runId: string) => apiGet<RunStory>(`/api/chat/workspaces/${ws}/runs/${runId}`),
};

/** How decisions reach a person by email (decision-email plan, part 1). */
export type DecisionEmails = "digest" | "instant" | "off";
